import { useState, useEffect } from 'preact/hooks'
import { useLocation } from 'wouter-preact'
import { checkAccessToken } from '@/services/api'
import {
  getToken,
  saveAvailableFreeSignatures,
  saveExpirationTime,
  saveAccessTokenType,
  saveErrorMessage,
} from '@/services/session'
import { useConfig } from '@/services/config'
import { usePageLoader } from '@/components'
import { AccessTokenInfoDtoType } from '@cencert/mob-signature'
import { getConfig } from '../../config'

/**
 * Access token validation hook
 *
 * Validates the access token on mount and manages related state:
 * - Checks token validity via API
 * - Handles free signature availability
 * - Manages token expiration time
 * - Handles validation errors and redirects
 *
 * @returns Object with validation status and results
 */
export interface AccessTokenValidation {
  isFreeSignatureAvailable: boolean | null
  isValidating: boolean
  validationError: string | null
}

export function useAccessTokenValidation(): AccessTokenValidation {
  const [, setLocation] = useLocation()
  const { setExpirationTime } = useConfig()
  const { showPageLoader, hidePageLoader } = usePageLoader()

  const [isFreeSignatureAvailable, setIsFreeSignatureAvailable] = useState<
    boolean | null
  >(null)
  const [isValidating, setIsValidating] = useState(false)
  const [validationError, setValidationError] = useState<string | null>(null)

  useEffect(() => {
    const validateToken = async () => {
      const token = getToken()
      const { requireTokenForAccess } = getConfig()

      if (!token) {
        if (requireTokenForAccess) {
          setLocation('/process-error?errorType=unauthorized-access')
          saveAvailableFreeSignatures(false)
          setIsFreeSignatureAvailable(false)
          return
        }

        saveAvailableFreeSignatures(false)
        setIsFreeSignatureAvailable(false)
        return
      }

      setIsValidating(true)
      showPageLoader()

      await checkAccessToken(token, {
        onSuccess: response => {
          const available = response.availableFreeSignatures ?? false
          const expirationTime = response.expirationTime ?? null
          const tokenType = response.type ?? AccessTokenInfoDtoType.DOCUMENT

          if (expirationTime) {
            saveExpirationTime(expirationTime)
            setExpirationTime(expirationTime)
          }

          saveAvailableFreeSignatures(available)
          saveAccessTokenType(tokenType)

          setIsFreeSignatureAvailable(available)
          setIsValidating(false)
          hidePageLoader()
        },
        onError: error => {
          hidePageLoader()
          setIsValidating(false)

          const hasTokenNotFoundError = error.response?.data?.errors?.some(
            (err: { code: string }) => err.code === 'TOKEN_NOT_FOUND'
          )

          if (hasTokenNotFoundError) {
            setLocation('/process-error?errorType=invalid-token')
          } else {
            const errorMessage = error.response?.data?.detail
            if (errorMessage) {
              saveErrorMessage(errorMessage)
              setValidationError(errorMessage)
            }
            setLocation('/process-error')
          }

          saveAvailableFreeSignatures(false)
          setIsFreeSignatureAvailable(false)
        },
      })
    }

    validateToken()
  }, [])

  return {
    isFreeSignatureAvailable,
    isValidating,
    validationError,
  }
}
